import { AfterViewChecked, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Agent, AGENT_PERMISSIONS } from '../../models/ai-agents.models';
import { AgentPermissionsService } from '../../services/agent-permissions.service';
import { TestChatClientService, TestChatDone } from '../../services/test-chat-client.service';

export interface ChatLine {
  /** NOTE: a small line of the runtime, e.g. "Read playbook: <title>" (contract v1.4). */
  role: 'USER' | 'ASSISTANT' | 'ERROR' | 'NOTE';
  text: string;
  streaming?: boolean;
  usage?: TestChatDone;
  ms?: number;
}

@Component({
  selector: 'app-ai-agent-test-chat',
  templateUrl: './agent-test-chat.component.html',
  styleUrls: ['../../ai-agents.shared.css', './agent-test-chat.component.css'],
})
export class AgentTestChatComponent implements OnDestroy, AfterViewChecked {
  @Input() agent!: Agent;
  @ViewChild('log') log?: ElementRef<HTMLDivElement>;

  readonly P = AGENT_PERMISSIONS;
  readonly maxChars = 2000;

  lines: ChatLine[] = [];
  text = '';
  conversationId: string | null = null;
  busy = false;
  private abort: AbortController | null = null;
  private scrollPending = false;

  constructor(private client: TestChatClientService, public perms: AgentPermissionsService, private router: Router) {}

  async send(): Promise<void> {
    const text = this.text.trim();
    if (!text || this.busy || text.length > this.maxChars) {
      return;
    }
    this.text = '';
    this.lines.push({ role: 'USER', text });
    const answer: ChatLine = { role: 'ASSISTANT', text: '', streaming: true };
    this.lines.push(answer);
    this.busy = true;
    this.scrollPending = true;
    this.abort = new AbortController();
    const started = performance.now();

    try {
      const result = await this.client.stream(
        this.agent.id,
        text,
        this.conversationId,
        {
          onConversationId: (id) => (this.conversationId = id),
          onDelta: (piece) => {
            answer.text += piece;
            this.scrollPending = true;
          },
          onDone: (done) => {
            answer.usage = done;
            if (done.conversationId) this.conversationId = done.conversationId;
          },
          onError: (message) => {
            this.lines.push({ role: 'ERROR', text: message });
            this.scrollPending = true;
          },
          onPlaybook: (playbook) => {
            this.addNoteBefore(answer, 'Read playbook: ' + playbook.title);
          },
        },
        this.abort.signal
      );
      if (result.conversationId) {
        this.conversationId = result.conversationId;
      }
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        this.lines.push({ role: 'ERROR', text: 'Connection failed: ' + (e?.message || e) });
      }
    } finally {
      answer.streaming = false;
      answer.ms = Math.round(performance.now() - started);
      if (!answer.text) {
        this.lines = this.lines.filter((l) => l !== answer);
      }
      this.busy = false;
      this.abort = null;
      this.scrollPending = true;
    }
  }

  stop(): void {
    this.abort?.abort();
  }

  /** The playbook event comes before the answer, so its note is shown above the answer bubble. */
  addNoteBefore(answer: ChatLine, text: string): void {
    const at = this.lines.indexOf(answer);
    const note: ChatLine = { role: 'NOTE', text };
    if (at < 0) this.lines.push(note);
    else this.lines.splice(at, 0, note);
    this.scrollPending = true;
  }

  newConversation(): void {
    this.stop();
    this.lines = [];
    this.conversationId = null;
  }

  openConversation(): void {
    if (this.conversationId) {
      this.router.navigate(['/ai-agents/conversations', this.conversationId]);
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) {
      event.preventDefault();
      this.send();
    }
  }

  ngAfterViewChecked(): void {
    if (this.scrollPending && this.log) {
      this.scrollPending = false;
      const el = this.log.nativeElement;
      el.scrollTop = el.scrollHeight;
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
